/**
 * StyTrix 訂閱方案 catalog — 單一真相來源（程式版）。
 *
 * 對應 docs/PRICING_PLANS.md。Landing pricing、Purchase Credits 彈窗、
 * 功能鎖（entitlement）、後端 plan 設定都應引用此檔，避免多套不一致的定價。
 *
 * 此檔為「純資料 + 純函式」，無任何 server/Node 依賴，可被前端與 Convex 共用。
 */

export type PlanId = 'free' | 'education' | 'starter' | 'pro' | 'team' | 'enterprise'

export const PLAN_IDS: PlanId[] = ['free', 'education', 'starter', 'pro', 'team', 'enterprise']
/** Plans that never bill through Stripe (no price, no trial). */
export const NON_BILLING_PLAN_IDS: PlanId[] = ['free', 'education']

/** 等級高低（用於 minPlan 比較：free < starter < pro < team < enterprise）。 */
export const PLAN_RANK: Record<PlanId, number> = {
  free: 0,
  education: 0, // same tier as free; tool access is decided per tool by `eduAllowed`
  starter: 1,
  pro: 2,
  team: 3,
  enterprise: 4,
}

/** 帳戶功能開關 key。 */
export type FeatureFlag =
  | 'watermark' // false = 去浮水印；free 帶浮水印
  | 'daily_limit' // false = 無每日上限；free 限 20 次/日
  | 'credits_rollover'
  | 'brand_training'
  | 'early_access'
  | 'multi_collab'
  | 'api_access'

export interface PlanDef {
  id: PlanId
  /** 顯示名稱（英文預設；本地化字串由 i18n 覆蓋）。 */
  name: string
  /** 月費 USD；null = 客製議價。 */
  monthlyFee: number | null
  /** 年繳總額 USD；null = 議價。 */
  annualTotal: number | null
  /** 年繳省 USD。 */
  annualSavings: number | null
  /** 每月配發 Credits；null = 客製。 */
  creditsMonthly: number | null
  /** 有效 $/cr（月費÷credits）；null = 不適用。 */
  effectivePrice: number | null
  /** 加購單價 $/cr；null = N/A 或議價。 */
  topupPrice: number | null
  /** 加購最低量；null = N/A 或客製。 */
  topupMin: number | null
  /** 訂閱 credits 累積上限（Pro 2250 / Team 4500）；null = 不累積或客製。 */
  rolloverCap: number | null
  /** 是否客製議價方案（Enterprise）。 */
  custom: boolean
  /** Stripe price lookup_key（test 模式 · mamoruAI）；free/enterprise 無。 */
  stripe: { monthly: string; yearly: string } | null
  /** 帳戶功能開關（true = 啟用該能力）。 */
  flags: Record<FeatureFlag, boolean>
  /** 帳戶資源規格。 */
  limits: {
    whiteboard: number | null // null = 無限
    storageGB: number | null // null = 客製
    maxTeam: number | null // null = 客製
    exportQuality: string
    exportFormats: string
    imgMaxRes: number // px
    vidMaxRes: string | null // null = 鎖定/不適用
    vidMaxDur: string | null // null = 鎖定/客製
  }
  /** 升級誘因（行銷訴求，英文預設；本地化由 i18n 覆蓋）。 */
  highlights: string[]
}

export const PLANS: Record<PlanId, PlanDef> = {
  free: {
    id: 'free',
    name: 'Free',
    monthlyFee: 0,
    annualTotal: 0,
    annualSavings: 0,
    creditsMonthly: 30,
    effectivePrice: null,
    topupPrice: null,
    topupMin: null,
    rolloverCap: null,
    custom: false,
    stripe: null,
    flags: {
      watermark: false, // 帶浮水印
      daily_limit: false, // 限 20 次/日
      credits_rollover: false,
      brand_training: false,
      early_access: false,
      multi_collab: false,
      api_access: false,
    },
    limits: {
      whiteboard: 1,
      storageGB: 1,
      maxTeam: 1,
      exportQuality: '標準',
      exportFormats: 'PNG',
      imgMaxRes: 1024,
      vidMaxRes: null,
      vidMaxDur: null,
    },
    highlights: ['免費體驗核心設計流程'],
  },
  education: {
    id: 'education',
    name: 'Education',
    monthlyFee: 0,
    annualTotal: 0,
    annualSavings: 0,
    creditsMonthly: 30,
    effectivePrice: null,
    topupPrice: null,
    topupMin: null,
    rolloverCap: null,
    custom: false,
    stripe: null,
    flags: {
      watermark: false,
      daily_limit: false,
      credits_rollover: false,
      brand_training: false,
      early_access: false,
      multi_collab: false,
      api_access: false,
    },
    limits: {
      whiteboard: 1,
      storageGB: 1,
      maxTeam: 1,
      exportQuality: '標準',
      exportFormats: 'PNG',
      imgMaxRes: 1024,
      vidMaxRes: null,
      vidMaxDur: null,
    },
    // Verified .edu / .ac email → 500 one-time credits; tools under 5 CR open by default.
    highlights: ['教育信箱驗證即享', '一次性 500 點', '核心工具（5 點以下）'],
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    monthlyFee: 19,
    annualTotal: 190,
    annualSavings: 38,
    creditsMonthly: 250,
    effectivePrice: 0.08,
    topupPrice: 0.1,
    topupMin: 50,
    rolloverCap: null,
    custom: false,
    stripe: { monthly: 'starter_monthly', yearly: 'starter_yearly' },
    flags: {
      watermark: true,
      daily_limit: true,
      credits_rollover: false,
      brand_training: false,
      early_access: false,
      multi_collab: false,
      api_access: false,
    },
    limits: {
      whiteboard: 3,
      storageGB: 5,
      maxTeam: 1,
      exportQuality: '高清',
      exportFormats: 'PNG/JPG/SVG',
      imgMaxRes: 2048,
      vidMaxRes: '720p',
      vidMaxDur: '5s',
    },
    highlights: ['完整 I2S 流程', '去浮水印', '用影片'],
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    monthlyFee: 49,
    annualTotal: 490,
    annualSavings: 98,
    creditsMonthly: 750,
    effectivePrice: 0.07,
    topupPrice: 0.1,
    topupMin: 50,
    rolloverCap: 2250,
    custom: false,
    stripe: { monthly: 'pro_monthly', yearly: 'pro_yearly' },
    flags: {
      watermark: true,
      daily_limit: true,
      credits_rollover: true,
      brand_training: false,
      early_access: false,
      multi_collab: false,
      api_access: false,
    },
    limits: {
      whiteboard: 5,
      storageGB: 20,
      maxTeam: 3,
      exportQuality: '高清',
      exportFormats: '全格式+PDF',
      imgMaxRes: 2048,
      vidMaxRes: '720p',
      vidMaxDur: '10s',
    },
    highlights: ['AI 棚拍', '風格訓練', 'Credits 可累積'],
  },
  team: {
    id: 'team',
    name: 'Team',
    monthlyFee: 99,
    annualTotal: 950,
    annualSavings: 238,
    creditsMonthly: 1500,
    effectivePrice: 0.07,
    topupPrice: 0.1,
    topupMin: 50,
    rolloverCap: 4500,
    custom: false,
    stripe: { monthly: 'team_monthly', yearly: 'team_yearly' },
    flags: {
      watermark: true,
      daily_limit: true,
      credits_rollover: true,
      brand_training: true,
      early_access: true,
      multi_collab: true,
      api_access: false,
    },
    limits: {
      whiteboard: 10,
      storageGB: 50,
      maxTeam: 10,
      exportQuality: '高清',
      exportFormats: '全部',
      imgMaxRes: 2048,
      vidMaxRes: '720p',
      vidMaxDur: '10s',
    },
    highlights: ['多人協作', '品牌級管理'],
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyFee: null,
    annualTotal: null,
    annualSavings: null,
    creditsMonthly: null,
    effectivePrice: null,
    topupPrice: null,
    topupMin: null,
    rolloverCap: null,
    custom: true,
    stripe: null,
    flags: {
      watermark: true,
      daily_limit: true,
      credits_rollover: true,
      brand_training: true,
      early_access: true,
      multi_collab: true,
      api_access: true,
    },
    limits: {
      whiteboard: null, // 無限
      storageGB: null, // 客製
      maxTeam: null, // 客製
      exportQuality: '4K+',
      exportFormats: '全部',
      imgMaxRes: 4096,
      vidMaxRes: '1080p',
      vidMaxDur: null, // 客製
    },
    highlights: ['API 整合', '大規模生產'],
  },
}

/**
 * 工具 credit 單價 + 各方案功能鎖。
 * `cost` = 每次消耗 credits；`minPlan` = 最低需要的方案（含以上皆解鎖）。
 * `toolId` 為對應 sidebar 的工具 id（部分為推測 / 子模式，見 docs/SIDEBAR_VISIBLE_TOOLS.md）。
 */
export interface ToolEntitlement {
  key: string // Excel 功能名稱
  group: '設計生成' | '圖像處理' | '視覺產出'
  cost: number // CR/次
  minPlan: PlanId
  toolId?: string // 對應 sidebar custom-id（可選 / 推測）
  tabId?: string // workflow/tab 層級計價：同一 toolId 下不同 workflow 可分開設價
  /** Education plan access. Unset = default rule `cost < EDU_DEFAULT_MAX_COST`. */
  eduAllowed?: boolean
  /** Receipt-gated API routes this tool's charge may be spent on (openspec: server-side-credit-enforcement). Unset = every image route; [] = none (ComfyUI / server-charged). */
  routes?: readonly string[]
  /** Upstream calls one charged unit may make (Multi-Angle: up to 8 views per unit). Default 1. */
  callsPerUnit?: number
}

/** Education plan default: tools costing this many credits or more are locked unless an admin opens them. */
export const EDU_DEFAULT_MAX_COST = 5

/** Effective Education access for a tool (explicit flag wins, otherwise the cost rule). */
export function eduAllowedFor(tool: Pick<ToolEntitlement, 'cost' | 'eduAllowed'>): boolean {
  return tool.eduAllowed ?? tool.cost < EDU_DEFAULT_MAX_COST
}

/**
 * The one gating rule (openspec: education-plan). Education is decided per tool
 * by `eduAllowed`; every other plan compares ranks against `minPlan`.
 */
export function isToolAllowedForPlan(
  tool: Pick<ToolEntitlement, 'cost' | 'minPlan' | 'eduAllowed'>,
  planId: PlanId,
): boolean {
  if (planId === 'education') return eduAllowedFor(tool)
  return PLAN_RANK[planId] >= PLAN_RANK[tool.minPlan]
}

/** Style Training's catalog id — the training actions charge it server-side (openspec: server-side-credit-charging). */
export const STYLE_TRAINING_TOOL_ID = 'custom-1761442977958'
/** Style Library's catalog id — default price for a generation with a trained style (Replicate) when the caller names no tool. */
export const STYLE_LIBRARY_TOOL_ID = 'custom-1770114725856'

export const TOOL_ENTITLEMENTS: ToolEntitlement[] = [
  // 設計生成
  // AI Chat = 右側 AI Chat 面板（component id "ai-chat-sidebar"），非 sidebar custom-* 工具
  { key: 'AI Chat 設計助手', group: '設計生成', cost: 1, minPlan: 'free', toolId: 'ai-chat-sidebar', routes: [] },
  // AI Chat 各模式單次生成計價（tabId = 模式）。/api/ai-chat-v2 依此扣點，
  // 送出鈕旁顯示同一數字；Agent 模式按各工具自身 toolId 計價，不在此列。
  { key: 'AI Chat — Image 圖片生成', group: '設計生成', cost: 2, minPlan: 'free', toolId: 'ai-chat-sidebar', tabId: 'image', routes: [] },
  { key: 'AI Chat — Design 設計頁', group: '設計生成', cost: 10, minPlan: 'free', toolId: 'ai-chat-sidebar', tabId: 'design', routes: [] },
  { key: 'AI Chat — Web 網頁生成', group: '設計生成', cost: 10, minPlan: 'free', toolId: 'ai-chat-sidebar', tabId: 'web', routes: [] },
  { key: 'CC — Photorealistic', group: '設計生成', cost: 1, minPlan: 'free', toolId: 'custom-1761621613302', tabId: 'tab-1761622112910-ssdbvzv9f', routes: ["/api/gemini","/api/nanobananapro","/api/gemini-flash-image"] },
  { key: 'CC — True to Sketch', group: '設計生成', cost: 1, minPlan: 'free', toolId: 'custom-1761621613302', tabId: 'tab-1761622114166-20avgxo6q', routes: ["/api/gemini","/api/nanobananapro","/api/gemini-flash-image"] },
  { key: 'CC — Balance', group: '設計生成', cost: 1, minPlan: 'free' },
  { key: 'Mix & Match', group: '設計生成', cost: 1, minPlan: 'free', toolId: 'custom-1769379472311', routes: ["/api/gemini","/api/nanobananapro","/api/gemini-flash-image"] },
  // Edit (image editing) — live sidebar tool since 2026-01; charged 10/image in the ledger (78 rows in 2026-09) but never listed here, so server-side charging priced it at 0 (hotfix 2026-10-01).
  { key: 'Edit 圖片編輯', group: '設計生成', cost: 10, minPlan: 'free', toolId: 'custom-1767056003753', routes: ["/api/gemini","/api/nanobananapro","/api/gemini-flash-image"] },
  { key: 'Model Library 虛擬人模', group: '設計生成', cost: 5, minPlan: 'free', toolId: 'custom-1761624828372', routes: ["/api/gemini","/api/nanobananapro","/api/gemini-flash-image"] },
  { key: 'Fabric Library 虛擬布料', group: '設計生成', cost: 5, minPlan: 'starter', toolId: 'custom-1761624837279', routes: ["/api/fabric-generation"] },
  { key: 'Style Library', group: '設計生成', cost: 5, minPlan: 'starter', toolId: 'custom-1770114725856', routes: ["/api/style-generation"] },
  // 圖像處理
  { key: 'Remove Background', group: '圖像處理', cost: 1, minPlan: 'free', toolId: 'custom-1762504453095', routes: [] },
  { key: 'Upscale 高清放大', group: '圖像處理', cost: 1, minPlan: 'starter', toolId: 'custom-1761524203806', routes: ["/api/pruna-upscale"] },
  { key: 'Split Layer 圖層分離', group: '圖像處理', cost: 2, minPlan: 'starter', toolId: 'custom-1773711624253', routes: ["/api/fal-layering"] },
  // ①③ get their own dedicated toolId (distinct from the tool's overall
  // sidebar id below) purely so the runtime cost lookup can resolve them —
  // they were never wired to any deduction before (2026-08-28 tester report:
  // the whole 3-step pipeline generated for free at every stage). ② keeps its
  // existing toolId unchanged, since that's also what gates the tool's
  // minPlan — don't fold ①③ into that same id or the gate would resolve to
  // whichever entry happens to come first instead of ②'s 'starter'.
  { key: 'I2S ① Flat Image', group: '圖像處理', cost: 1, minPlan: 'free', toolId: 'i2s-flat-image', routes: ["/api/gemini"] },
  { key: 'I2S ② Image to Sketch', group: '圖像處理', cost: 1, minPlan: 'starter', toolId: 'custom-1772528684208', routes: ["/api/gemini"] },
  { key: 'I2S ③ Sketch to Vector', group: '圖像處理', cost: 3, minPlan: 'starter', toolId: 'i2s-sketch-to-vector', routes: ["/api/fal-vectorize"] },
  // 視覺產出
  // Group Shoot AI 棚拍 = Photoshooting 工具（依用戶確認）
  { key: 'Group Shoot AI 棚拍', group: '視覺產出', cost: 1, minPlan: 'pro', toolId: 'custom-1772518001040', routes: ["/api/gemini","/api/nanobananapro","/api/gemini-product-views"] },
  { key: 'Multi-Angle 多角度', group: '視覺產出', cost: 5, minPlan: 'pro', toolId: 'custom-1776991334106', routes: ["/api/gemini-product-views"], callsPerUnit: 8 },
  // 5s/10s 共用 toolId，用 tabId 區分計價層級（否則 getToolCostByToolId 在未帶
  // tabId 查詢時，兩筆同 toolId 的紀錄會永遠命中陣列中第一筆 = 5s 價格）。
  { key: 'Video Generation 5s', group: '視覺產出', cost: 40, minPlan: 'starter', toolId: 'custom-1761467576166', tabId: '5s', routes: ["/api/xai-video/generate"] },
  { key: 'Video Generation 10s', group: '視覺產出', cost: 80, minPlan: 'pro', toolId: 'custom-1761467576166', tabId: '10s', routes: ["/api/xai-video/generate"] },
  { key: 'Style Training 風格訓練', group: '視覺產出', cost: 100, minPlan: 'pro', toolId: 'custom-1761442977958', routes: [] },
]

// ───────────────────────── helpers ─────────────────────────

export function getPlan(planId: PlanId): PlanDef {
  return PLANS[planId]
}

/** 該方案是否解鎖某工具（依 Excel 功能名 + minPlan 等級比較）。 */
export function isToolUnlocked(toolKey: string, planId: PlanId): boolean {
  const tool = TOOL_ENTITLEMENTS.find((t) => t.key === toolKey)
  if (!tool) return true // 未列管的工具預設開放
  return isToolAllowedForPlan(tool, planId)
}

/** 該方案是否啟用某功能開關。 */
export function hasFlag(planId: PlanId, flag: FeatureFlag): boolean {
  return PLANS[planId].flags[flag]
}

/** planId 是否 >= 指定最低方案。 */
export function meetsPlan(planId: PlanId, minPlan: PlanId): boolean {
  return PLAN_RANK[planId] >= PLAN_RANK[minPlan]
}

/** 取某工具的 credit 單價（找不到回 0）。 */
export function getToolCost(toolKey: string): number {
  return TOOL_ENTITLEMENTS.find((t) => t.key === toolKey)?.cost ?? 0
}

/** 用 sidebar toolId（custom-*）判斷某方案是否解鎖該工具。未列管 → 預設開放。 */
export function isToolIdUnlocked(toolId: string, planId: PlanId): boolean {
  const tool = TOOL_ENTITLEMENTS.find((t) => t.toolId === toolId)
  if (!tool) return true
  return isToolAllowedForPlan(tool, planId)
}

/** 取某 toolId 的最低方案需求（未列管回 'free'）。 */
export function getToolMinPlan(toolId: string): PlanId {
  return TOOL_ENTITLEMENTS.find((t) => t.toolId === toolId)?.minPlan ?? 'free'
}

/**
 * DB-config variants: gate by a provided toolEntitlements list (admin-edited),
 * so the display layer can reflect getPlanConfig without re-deploying code.
 */
export function isToolIdUnlockedIn(
  toolEntitlements: Pick<ToolEntitlement, 'toolId' | 'minPlan' | 'cost' | 'eduAllowed'>[],
  toolId: string,
  planId: PlanId,
): boolean {
  const tool = toolEntitlements.find((t) => t.toolId === toolId)
  if (!tool) return true
  return isToolAllowedForPlan(tool, planId)
}

export function getToolMinPlanIn(
  toolEntitlements: Pick<ToolEntitlement, 'toolId' | 'minPlan'>[],
  toolId: string,
): PlanId {
  return toolEntitlements.find((t) => t.toolId === toolId)?.minPlan ?? 'free'
}

// ─────────────── overrides-merge model (admin config) ───────────────
// Structure + defaults always come from this file; the DB stores only deltas.

export type ToolOverride = { cost?: number; minPlan?: PlanId; eduAllowed?: boolean }
// Stored as an ARRAY (not an object map) because override keys are feature
// names with non-ASCII chars (em-dash, Chinese), which Convex forbids as object
// field names.
export type ToolOverrideEntry = { key: string; cost?: number; minPlan?: PlanId; eduAllowed?: boolean }

/**
 * Canonical override key for a tool entitlement: the Excel feature `key`, which
 * is UNIQUE per catalog entry (unlike toolId, which several entries share —
 * e.g. Video 5s/10s, or CC workflows). Stable across code changes.
 */
export function entKey(t: Pick<ToolEntitlement, 'key'>): string {
  return t.key
}

/**
 * Override key for a runtime (toolId[, tabId]) lookup: map it to the matching
 * catalog entry's feature key; if unlisted, a synthetic toolId-based key.
 */
export function keyForTool(toolId: string, tabId?: string): string {
  let t = tabId
    ? TOOL_ENTITLEMENTS.find((x) => x.toolId === toolId && x.tabId === tabId)
    : undefined
  if (!t) t = TOOL_ENTITLEMENTS.find((x) => x.toolId === toolId && !x.tabId)
  if (!t) t = TOOL_ENTITLEMENTS.find((x) => x.toolId === toolId)
  if (t) return t.key
  return tabId ? `${toolId}::${tabId}` : toolId
}

/** Merge code default plans with admin plan overrides → effective plans. */
export function mergePlans(
  planOverrides?: Record<string, any> | null,
): Record<PlanId, PlanDef> {
  const out = {} as Record<PlanId, PlanDef>
  for (const id of PLAN_IDS) {
    const o = (planOverrides && planOverrides[id]) || {}
    out[id] = {
      ...PLANS[id],
      ...o,
      flags: { ...PLANS[id].flags, ...(o.flags || {}) },
      limits: { ...PLANS[id].limits, ...(o.limits || {}) },
    }
  }
  return out
}

/** Merge code default tool entitlements with admin cost/minPlan overrides. */
export function mergeToolEntitlements(
  toolOverrides?: ToolOverrideEntry[] | null,
): ToolEntitlement[] {
  const list = Array.isArray(toolOverrides) ? toolOverrides : []
  const ov: Record<string, ToolOverrideEntry> = {}
  for (const e of list) if (e && typeof e.key === 'string') ov[e.key] = e
  const result: ToolEntitlement[] = TOOL_ENTITLEMENTS.map((t) => {
    const o = ov[entKey(t)]
    if (!o) return { ...t }
    return {
      ...t,
      ...(o.cost != null ? { cost: o.cost } : {}),
      ...(o.minPlan ? { minPlan: o.minPlan } : {}),
      ...(typeof o.eduAllowed === 'boolean' ? { eduAllowed: o.eduAllowed } : {}),
    }
  })
  // Keep override-only entries with no code-catalog counterpart yet (e.g. a
  // per-workflow cost set from the canvas for an unlisted tool — key is a
  // synthetic toolId or toolId::tabId).
  const known = new Set(TOOL_ENTITLEMENTS.map((t) => entKey(t)))
  for (const e of list) {
    if (known.has(e.key)) continue
    const [toolId, tabId] = e.key.includes('::') ? e.key.split('::') : [e.key, undefined]
    result.push({
      key: e.key,
      group: '視覺產出',
      cost: e.cost ?? 0,
      minPlan: e.minPlan ?? 'free',
      ...(typeof e.eduAllowed === 'boolean' ? { eduAllowed: e.eduAllowed } : {}),
      toolId,
      ...(tabId ? { tabId } : {}),
    })
  }
  return result
}

/**
 * Fixed credit top-up packs (client-confirmed; see Q14).
 * Large pack is discounted vs per-credit rate to upsell toward upgrade.
 */
export const TOPUP_PACKS = [
  { label: 'Small',  credits: 50,  priceUSD: 5  },
  { label: 'Medium', credits: 150, priceUSD: 15 },
  { label: 'Large',  credits: 300, priceUSD: 25 },
] as const

export type TopupPack = typeof TOPUP_PACKS[number]

/**
 * The Free plan cannot buy top-up packs — extra credits come with a paid plan.
 * One rule for the checkout action and the Purchase modal. Top-up credits a
 * Free account already holds are untouched.
 */
export function canBuyTopup(planId: string | null | undefined): boolean {
  return (planId ?? 'free') !== 'free'
}

export const TOPUP_FREE_PLAN_MESSAGE =
  'Credit top-ups are available on paid plans. Upgrade your plan to buy extra credits.'

/** Valid credit quantities for a top-up (server-side guard). */
export const TOPUP_VALID_CREDITS = TOPUP_PACKS.map((p) => p.credits)

/**
 * 加購（top-up）設定：可加購回 { pricePerCredit, minQty }，否則 null
 * （free 需先升級、enterprise 議價）。見 docs/CREDIT_TOPUP.md。
 */
export function getTopup(planId: PlanId): { pricePerCredit: number; minQty: number } | null {
  const p = PLANS[planId]
  if (p.topupPrice == null || p.topupMin == null) return null
  return { pricePerCredit: p.topupPrice, minQty: p.topupMin }
}
